import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import { KebabMenu } from "../../components/KebabMenu";
import { ModalShell } from "../../components/ModalShell";
import { Toast, toneForMessage, useToast } from "../../components/Toast";
import { useAuth } from "../../hooks/useAuth";
import { type AdminUser, useAdminUsers } from "../../hooks/useAdminUsers";
import { useImpersonation } from "../../hooks/useImpersonation";
import { useInvites } from "../../hooks/useInvites";
import { isValidEmail } from "../../utils/email";
import { formatDateTime, formatRelativeTime } from "../../utils/formatDateTime";
import styles from "../AdminInstances.module.css";
import flagStyles from "./AdminFeatureFlags.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

const statusBadge = (status: AdminUser["status"]): string => {
  switch (status) {
    case "active": return "Active";
    case "invited": return "Invited";
    case "revoked": return "Revoked";
    case "inactive": return "Inactive";
  }
};

// Most-engaged first: instance owners on top, then most recently seen.
const compareUsers = (a: AdminUser, b: AdminUser): number => {
  if (a.instanceCount !== b.instanceCount) return b.instanceCount - a.instanceCount;
  const aSeen = a.lastSeenAt ? Date.parse(a.lastSeenAt) : 0;
  const bSeen = b.lastSeenAt ? Date.parse(b.lastSeenAt) : 0;
  if (aSeen !== bSeen) return bSeen - aSeen;
  return a.email.localeCompare(b.email);
};

export const AdminUsers = (): JSX.Element => {
  const {
    status, users, errorMessage, refetch,
    setBudget, clearBudget, setInstanceMemberCap, clearInstanceMemberCap,
    setCategory, clearCategory, deleteUser, disableLogin, enableLogin,
  } = useAdminUsers();
  const { createInvite, revokeInvite, refresh: refreshInvites } = useInvites();
  const { start: startImpersonation } = useImpersonation();
  const { user: currentUser } = useAuth();

  const { toast, showToast, dismissToast } = useToast();
  const setFlash = useCallback((message: string) => {
    showToast(message, toneForMessage(message));
  }, [showToast]);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [editingMemberCap, setEditingMemberCap] = useState<AdminUser | null>(null);
  const [inviteOpen, setInviteOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<AdminUser | null>(null);
  const [impersonating, setImpersonating] = useState<string | null>(null);

  const handleImpersonate = async (email: string): Promise<void> => {
    setImpersonating(email);
    dismissToast();
    const result = await startImpersonation(email);
    if (result.ok) {
      // Land on the user's overview so the admin sees what they see.
      window.location.href = "/dashboard/overview";
      return;
    }
    setFlash(`Impersonation failed: ${result.message}`);
    setImpersonating(null);
  };

  const totalActive = useMemo(() => users.filter((u) => u.status === "active").length, [users]);

  // Admins (from the auth provider's admins group) take precedence and sit
  // at the very top, then internal, then customers. A user is only ever in
  // one group.
  const { admins, customers, internal } = useMemo(() => {
    const sorted = [...users].sort(compareUsers);
    return {
      admins: sorted.filter((u) => u.isAdmin),
      internal: sorted.filter((u) => !u.isAdmin && u.category === "internal"),
      customers: sorted.filter((u) => !u.isAdmin && u.category !== "internal"),
    };
  }, [users]);

  const handleSendInvite = async (email: string): Promise<{ ok: boolean; message: string }> => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return { ok: false, message: "Email is required." };
    const result = await createInvite(trimmed);
    if (result.ok) {
      await refetch();
      return { ok: true, message: `Invited ${trimmed}.` };
    }
    return { ok: false, message: result.message };
  };

  const handleRevoke = async (email: string): Promise<void> => {
    const result = await revokeInvite(email);
    if (result.ok) {
      setFlash(`Revoked invite for ${email}.`);
      await Promise.all([refreshInvites(), refetch()]);
    } else {
      setFlash(`Revoke failed: ${result.message}`);
    }
  };

  const handleSaveBudget = async (email: string, budget: number, override: boolean): Promise<void> => {
    try {
      if (override) {
        await setBudget(email, budget);
        setFlash(`Set ${email} budget to ${budget}.`);
      } else {
        await clearBudget(email);
        setFlash(`Cleared ${email} budget override.`);
      }
      setEditing(null);
    } catch (err) {
      setFlash(`Save failed: ${err instanceof Error ? err.message : "unknown"}`);
    }
  };

  const handleSaveMemberCap = async (email: string, cap: number, override: boolean): Promise<void> => {
    try {
      if (override) {
        await setInstanceMemberCap(email, cap);
        setFlash(`Set ${email} collaborator cap to ${cap}.`);
      } else {
        await clearInstanceMemberCap(email);
        setFlash(`Cleared ${email} collaborator cap override.`);
      }
      setEditingMemberCap(null);
    } catch (err) {
      setFlash(`Save failed: ${err instanceof Error ? err.message : "unknown"}`);
    }
  };

  const handleToggleCategory = async (user: AdminUser): Promise<void> => {
    try {
      if (user.category === "internal") {
        await clearCategory(user.email);
        setFlash(`Moved ${user.email} to customers.`);
      } else {
        await setCategory(user.email, "internal");
        setFlash(`Marked ${user.email} as internal.`);
      }
    } catch (err) {
      setFlash(`Save failed: ${err instanceof Error ? err.message : "unknown"}`);
    }
  };

  const handleToggleLogin = async (user: AdminUser): Promise<void> => {
    if (user.loginDisabled) {
      const result = await enableLogin(user.email);
      setFlash(result.ok ? `Re-enabled login for ${user.email}.` : `Enable failed: ${result.message}`);
    } else {
      const result = await disableLogin(user.email);
      setFlash(result.ok ? `Blocked login for ${user.email}.` : `Block failed: ${result.message}`);
    }
  };

  const handleDelete = async (email: string): Promise<void> => {
    const result = await deleteUser(email);
    if (result.ok) {
      setFlash(`Deleted ${email}.`);
      setDeleting(null);
    } else {
      setFlash(`Delete failed: ${result.message}`);
      setDeleting(null);
    }
  };

  const renderRow = (u: AdminUser): JSX.Element => {
    const isSelf = u.email === currentUser?.email;
    return (
      <tr key={u.email}>
        <td className={styles.owner}>
          <span className={`${styles.ownerEmail} ${styles.ownerEmailWide}`} title={u.email}>
            {u.email}
          </span>
        </td>
        <td>
          <div className={styles.statusCell}>
            <span className={`${styles.pill} ${styles[`pill_${u.status.toUpperCase()}`] ?? ""}`}>
              {statusBadge(u.status)}
            </span>
            {u.loginDisabled && (
              <span
                className={`${styles.pill} ${styles.pill_BLOCKED ?? ""}`}
                title="Cognito login disabled — this user can't sign in"
              >
                Blocked
              </span>
            )}
          </div>
        </td>
        <td>{u.instanceCount}</td>
        <td className={styles.muted}>
          {u.lastSeenAt ? (
            <span title={formatDateTime(u.lastSeenAt)}>{formatRelativeTime(u.lastSeenAt)}</span>
          ) : (
            "—"
          )}
        </td>
        <td className={styles.muted}>
          {u.lastLoginAt ? (
            <span title={formatDateTime(u.lastLoginAt)}>{formatRelativeTime(u.lastLoginAt)}</span>
          ) : (
            "—"
          )}
        </td>
        <td className={styles.muted}>
          {u.instanceBudget}
          {u.instanceBudgetOverridden && <span className={styles.overrideTag}>override</span>}
        </td>
        <td className={styles.muted}>
          {u.instanceMemberCap}
          {u.instanceMemberCapOverridden && <span className={styles.overrideTag}>override</span>}
        </td>
        <td className={styles.actionCell}>
          <div className={styles.actionCellInner}>
            {impersonating === u.email && (
              <span
                className={`${styles.actionStatus} ${styles.actionSpinner}`}
                role="status"
                aria-label={`Opening the dashboard as ${u.email}`}
              />
            )}
            <KebabMenu
              ariaLabel={`Actions for ${u.email}`}
              items={[
                {
                  label: "Edit budget",
                  onClick: () => setEditing(u),
                },
                {
                  label: "Edit collaborator cap",
                  onClick: () => setEditingMemberCap(u),
                },
                {
                  label: "View as user",
                  onClick: () => void handleImpersonate(u.email),
                  disabled: impersonating === u.email || isSelf,
                  ...(isSelf ? { disabledReason: "You can't impersonate yourself" } : {}),
                },
                {
                  label: u.category === "internal" ? "Move to customers" : "Mark as internal",
                  onClick: () => void handleToggleCategory(u),
                },
                {
                  label: u.loginDisabled ? "Enable login" : "Disable login",
                  onClick: () => void handleToggleLogin(u),
                  ...(u.loginDisabled ? {} : { destructive: true }),
                  disabled: isSelf,
                  ...(isSelf ? { disabledReason: "You can't disable your own login" } : {}),
                },
                ...(u.status === "invited"
                  ? [{ label: "Revoke invite", destructive: true, onClick: () => void handleRevoke(u.email) }]
                  : []),
                {
                  label: "Delete user",
                  destructive: true,
                  onClick: () => setDeleting(u),
                  disabled: u.instanceCount > 0 || isSelf,
                  ...(isSelf
                    ? { disabledReason: "You can't delete yourself" }
                    : u.instanceCount > 0
                      ? { disabledReason: "Delete or reassign their instances first" }
                      : {}),
                },
              ]}
            />
          </div>
        </td>
      </tr>
    );
  };

  const renderSection = (label: string, rows: ReadonlyArray<AdminUser>): JSX.Element => (
    <tbody>
      <tr className={styles.sectionRow}>
        <td colSpan={8}>
          {label} ({rows.length})
        </td>
      </tr>
      {rows.map(renderRow)}
    </tbody>
  );

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Users">
        Everyone with a login or an open invite. Budgets, collaborator caps, impersonation and deletion are in each row&apos;s menu.
      </AdminPageIntro>

      <div className={styles.toolbar}>
        <div className={styles.toolbarMeta}>
          {status === "ready"
            ? `${users.length} users · ${admins.length} admins · ${totalActive} active`
            : "Loading…"}
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => setInviteOpen(true)}
        >
          Invite user
        </button>
      </div>

      <section className={styles.listSection}>
        {status === "loading" && (
          <AdminTableSkeleton
            headers={["Email", "Status", "Instances", "Last active", "Last login", "Budget", "Collab cap", ""]}
            tableClassName={styles.table ?? ""}
          />
        )}
        {status === "error" && (
          <div className={styles.empty}>{errorMessage ?? "Failed to load."}</div>
        )}
        {status === "ready" && users.length === 0 && (
          <div className={styles.empty}>No users yet — invite one to get started.</div>
        )}
        {status === "ready" && users.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Instances</th>
                <th>Last active</th>
                <th>Last login</th>
                <th>Budget</th>
                <th title="Collaborator cap per instance">Collab cap</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            {admins.length > 0 || internal.length > 0 ? (
              <>
                {admins.length > 0 && renderSection("Admins", admins)}
                {renderSection("Customers", customers)}
                {internal.length > 0 && renderSection("Internal", internal)}
              </>
            ) : (
              <tbody>{customers.map(renderRow)}</tbody>
            )}
          </table>
        )}
      </section>

      {editing && (
        <BudgetDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSave={(budget, override) => void handleSaveBudget(editing.email, budget, override)}
        />
      )}

      {editingMemberCap && (
        <MemberCapDialog
          user={editingMemberCap}
          onClose={() => setEditingMemberCap(null)}
          onSave={(cap, override) => void handleSaveMemberCap(editingMemberCap.email, cap, override)}
        />
      )}

      {deleting && (
        <DeleteUserDialog
          user={deleting}
          onClose={() => setDeleting(null)}
          onConfirm={() => void handleDelete(deleting.email)}
        />
      )}

      {inviteOpen && (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onSend={async (email) => {
            const result = await handleSendInvite(email);
            if (result.ok) {
              setFlash(result.message);
              setInviteOpen(false);
            }
            return result;
          }}
        />
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

type DeleteUserDialogProps = {
  user: AdminUser;
  onClose: () => void;
  onConfirm: () => void;
};

const DeleteUserDialog = ({ user, onClose, onConfirm }: DeleteUserDialogProps): JSX.Element => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const titleId = `delete-user-dialog-title-${user.email}`;

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId}>
      <header className={flagStyles.dialogHeader}>
        <h3 className={flagStyles.dialogTitle} id={titleId}>
          Delete user — <code>{user.email}</code>
        </h3>
        <button type="button" className={flagStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <p className={flagStyles.hint}>
        Removes their invite, login, overrides, and any instance collaborations.
        This can&apos;t be undone — they&apos;d need a fresh invite to come back.
      </p>

      <footer className={flagStyles.footer}>
        <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.dangerBtn}
          disabled={submitting}
          onClick={() => {
            setSubmitting(true);
            onConfirm();
          }}
        >
          {submitting ? "Deleting…" : "Delete user"}
        </button>
      </footer>
    </ModalShell>
  );
};

type BudgetDialogProps = {
  user: AdminUser;
  onClose: () => void;
  onSave: (budget: number, override: boolean) => void;
};

const BudgetDialog = ({ user, onClose, onSave }: BudgetDialogProps): JSX.Element => {
  const [budget, setBudget] = useState<number>(user.instanceBudget);
  const [override, setOverride] = useState<boolean>(user.instanceBudgetOverridden);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const titleId = `budget-dialog-title-${user.email}`;

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId}>
      <header className={flagStyles.dialogHeader}>
          <h3 className={flagStyles.dialogTitle} id={titleId}>
            Edit instance budget — <code>{user.email}</code>
          </h3>
          <button type="button" className={flagStyles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave(budget, override);
          }}
        >
          <label className={flagStyles.checkboxField}>
            <input
              type="checkbox"
              checked={override}
              onChange={(e) => setOverride(e.target.checked)}
            />
            <span>Set per-user override</span>
          </label>

          <label className={flagStyles.field}>
            <span>Budget</span>
            <input
              ref={inputRef}
              type="number"
              min={0}
              max={1000}
              value={budget}
              disabled={!override}
              onChange={(e) => setBudget(Number(e.target.value))}
              required={override}
            />
          </label>

          <p className={flagStyles.hint}>
            Per-user override wins over the global default. Disable the override to revert this
            user to the platform default.
          </p>

          <footer className={flagStyles.footer}>
            <button type="button" className={styles.secondaryBtn} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.primaryBtn}>
              Save
            </button>
          </footer>
        </form>
    </ModalShell>
  );
};

type MemberCapDialogProps = {
  user: AdminUser;
  onClose: () => void;
  onSave: (cap: number, override: boolean) => void;
};

const MemberCapDialog = ({ user, onClose, onSave }: MemberCapDialogProps): JSX.Element => {
  const [cap, setCap] = useState<number>(user.instanceMemberCap);
  const [override, setOverride] = useState<boolean>(user.instanceMemberCapOverridden);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const titleId = `member-cap-dialog-title-${user.email}`;

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId}>
      <header className={flagStyles.dialogHeader}>
        <h3 className={flagStyles.dialogTitle} id={titleId}>
          Edit collaborator cap — <code>{user.email}</code>
        </h3>
        <button type="button" className={flagStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(cap, override);
        }}
      >
        <label className={flagStyles.checkboxField}>
          <input
            type="checkbox"
            checked={override}
            onChange={(e) => setOverride(e.target.checked)}
          />
          <span>Set per-user override</span>
        </label>

        <label className={flagStyles.field}>
          <span>Max collaborators per instance</span>
          <input
            ref={inputRef}
            type="number"
            min={0}
            max={1000}
            value={cap}
            disabled={!override}
            onChange={(e) => setCap(Number(e.target.value))}
            required={override}
          />
        </label>

        <p className={flagStyles.hint}>
          How many collaborators this customer can invite to each of their instances
          (excluding themselves). Applies when their instance_sharing flag is enabled.
          Disable the override to revert to the platform default.
        </p>

        <footer className={flagStyles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={styles.primaryBtn}>
            Save
          </button>
        </footer>
      </form>
    </ModalShell>
  );
};

type InviteDialogProps = {
  onClose: () => void;
  onSend: (email: string) => Promise<{ ok: boolean; message: string }>;
};

const InviteDialog = ({ onClose, onSend }: InviteDialogProps): JSX.Element => {
  const [email, setEmail] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const validEmail = isValidEmail(email);
  const titleId = "invite-dialog-title";

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId}>
      <header className={flagStyles.dialogHeader}>
          <h3 className={flagStyles.dialogTitle} id={titleId}>Invite user</h3>
          <button type="button" className={flagStyles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!validEmail) {
              setError("Enter a valid email address.");
              return;
            }
            setSubmitting(true);
            setError(null);
            const result = await onSend(email);
            setSubmitting(false);
            if (!result.ok) setError(result.message);
          }}
        >
          <label className={flagStyles.field}>
            <span>Customer email</span>
            <input
              ref={inputRef}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              required
              disabled={submitting}
            />
          </label>

          {error && <p className={flagStyles.errorBanner}>{error}</p>}

          <footer className={flagStyles.footer}>
            <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={submitting || !validEmail}
              title={!validEmail ? "Enter a valid email" : undefined}
            >
              {submitting ? "Sending…" : "Send invite"}
            </button>
          </footer>
        </form>
    </ModalShell>
  );
};
